import { useState } from 'react';
import Button from 'src/components/buttons/button';
import NumberedList from 'src/components/numberedList';
import config from 'config/config';
import Icon from 'src/components/icon';

const PROGRAM_URL = `${config.cdn.navigator}living-with-high-blood-pressure/`;

const getMenu = () => (
  <section className="navigator__contents">
    <div className="navigator__contents-header">Contents</div>
    <ul className="navigator__contents__list">
      <li><div className="navigator__contents__number">1.</div><div className="navigator__contents__content-text"><a href="#1">Why is too much salt bad for you?</a></div></li>
      <li><div className="navigator__contents__number">2.</div><div className="navigator__contents__content-text"><a href="#2">How much salt is too much?</a></div></li>
      <li><div className="navigator__contents__number">3.</div><div className="navigator__contents__content-text"><a href="#3">7 salt slashing tips</a></div></li>
      <li><div className="navigator__contents__number">4.</div><div className="navigator__contents__content-text"><a href="#4">Get the full guide to cutting back salt</a></div></li>
    </ul>
  </section>
);

const getPage = () => (
  <div className="navigator">
    <section className="navigator__content">
      <h1 className="navigator__title">
        {'Week 5: How to eat less salt'}
      </h1>
      <p>{`A diet that's high in salt is likely to drive your blood pressure higher and increase your risk of heart and circulatory disease. Many of us eat too much salt without even realising it. This week we'll give you information on how to keep track of hidden salt and the changes you can make to improve your heart health.`}</p>
      <div className="visible-xs">{getMenu()}</div>

      <h2>Why is too much salt bad for you?</h2>
      <p>{`Salt has been a part of our diet for years and it's eaten all around the world. But too much salt does have an impact on your body and cutting back will help contribute to healthier blood pressure levels.`}</p>
      <div className="navigator__video-container">
        <iframe
          allow="autoplay; encrypted-media; fullscreen"
          src="https://www.youtube.com/embed/uM8yQNZ0x10"
        />
      </div>

      <h2>How much salt is too much?</h2>
      <p>
        Eating too much salt is linked with higher blood pressure - it disrupts the balance in your body and makes your body hold on to the water inside you,
        putting pressure on your blood vessels.
      </p>
      <p>Cutting back on salt can help you reduce water retention and improve your blood pressure.</p>
      <p>{`Adults should eat no more than 6g of salt a day, and that includes salt that's already in your food as well as the salt you put on your meals. Lots of foods have hidden salt, even ones that don't taste salty.`}</p>
      <img alt="6 grams is a level teaspoon" className="navigator__image-full" src={`${PROGRAM_URL}wk5-6g.png`} />
      <p>Start keeping track of the foods you eat often and how much salt is in them.</p>
      <a href="https://www.bhf.org.uk/heart-matters-magazine/nutrition/sugar-salt-and-fat/salt-questions-answered" rel="noreferrer" target="_blank">
        Read more about salt and your heart health {'>'}
      </a>

      <h2>7 salt slashing tips</h2>
      <NumberedList items={[
        `Don't put salt on the table - get used to meals without extra salt`,
        'Buy reduced-salt versions of store cupboard ingredients like stock cubes, soy sauce, and canned goods like baked beans.',
        'Pick packaged foods that are labeled as low-salt or low-sodium.',
        'Reduce the amount of takeaways you have. They are often high in salt, so only have them only as an occasional treat.',
        `If you're eating out, ask for sauces and dressings to be served on the side, so you can use less of them.`,
        'For seasoning, replace salt with herbs, spices and citrus juices. Experiment with different flavours.',
        `Reduce the amount of salt you eat gradually - you'll be more likely to make the changes last.`]
      }
      />
      <a href="https://www.bhf.org.uk/heart-matters-magazine/nutrition/sugar-salt-and-fat/salty-surprises" rel="noreferrer" target="_blank">
        Find out about these 7 surprisingly salty foods {'>'}
      </a>

      <h2>The BHF guide to cutting down on salt</h2>
      <p>Download and keep this handy guide, bringing together all our salt shaking tips in one place.</p>
      <a href="https://www.bhf.org.uk/heart-health/preventing-heart-disease/healthy-eating/salt" rel="noreferrer" target="_blank">
        <Button
          icon="open-right"
          iconLeftAligned={false}
          text="Get the guide"
        />
      </a>

      <img alt="Content provided by BHF" className="navigator__image-full navigator__banner-footer" src={`${PROGRAM_URL}bhf-content.jpg`} />
    </section>
  </div>
);

function Footer() {
  const [visible, setVisible] = useState(true);

  if (!visible) return null;
  return (
    <div className="navigator__footer">
      <img alt="HealthUnlocked British Heart Foundation community screenshot" className="navigator__footer__image" src={`${PROGRAM_URL}cta-community.png`} />
      <div className="navigator__footer__inner">
        <div className="navigator__footer__title">
          Join the conversation on HealthUnlocked
        </div>
        <div className="navigator__footer__description">
          {`To get the most out of your heart health journey, you'll need support. Our online community on HealthUnlocked brings patients, their loved-ones and healthcare experts all together online to share advice and encouragement.`}
          {' '}
          <a
            className="navigator__footer__link"
            href="https://healthunlocked.com/bhf"
            rel="noreferrer"
            target="_blank"
          >
            Join the conversation {'>'}
          </a>
        </div>
        <div className="navigator__footer__close" onClick={() => setVisible(false)}>
          <Icon icon="cross" />
        </div>
      </div>
    </div>
  );
}

const obj = {
  getPage,
  getMenu,
  Footer,
  getSeoTitle: 'Living with high blood pressure | HealthUnlocked',
};

export default obj;
